// ─── What: time-domain zoom & pan math ────────────────────────────────────────

export const MIN_SPAN_MS = 60_000;

// ─── How it works ─────────────────────────────────────────────────────────────

/** Zoom a time domain toward a cursor time. factor<1 zooms in, >1 zooms out. */
export function zoomDomain(
    domain: [Date, Date],
    cursorTime: Date,
    factor: number,
    bounds: [Date, Date]
): [Date, Date] {
    const t0 = domain[0].getTime();
    const t1 = domain[1].getTime();
    const c = cursorTime.getTime();
    let left = c - (c - t0) * factor;
    let right = c + (t1 - c) * factor;

    if (right - left < MIN_SPAN_MS) {
        const mid = (left + right) / 2;
        left = mid - MIN_SPAN_MS / 2;
        right = mid + MIN_SPAN_MS / 2;
    }

    const lo = bounds[0].getTime();
    const hi = bounds[1].getTime();
    if (left < lo) left = lo;
    if (right > hi) right = hi;
    return [new Date(left), new Date(right)];
}

/**
 * True when two domains are indistinguishable on screen. `toleranceMs` should be
 * one pixel's worth of time: the "now" bound advances between renders, so exact
 * equality would call every return to the live edge a real change.
 */
export function sameDomain(
    a: [Date, Date],
    b: [Date, Date],
    toleranceMs = 0
): boolean {
    return (
        Math.abs(a[0].getTime() - b[0].getTime()) <= toleranceMs &&
        Math.abs(a[1].getTime() - b[1].getTime()) <= toleranceMs
    );
}

/** Shift a time domain by deltaMs, preserving its span, clamped to bounds. */
export function panDomain(
    domain: [Date, Date],
    deltaMs: number,
    bounds: [Date, Date]
): [Date, Date] {
    let left = domain[0].getTime() + deltaMs;
    let right = domain[1].getTime() + deltaMs;
    const span = right - left;
    const lo = bounds[0].getTime();
    const hi = bounds[1].getTime();
    if (left < lo) {
        left = lo;
        right = lo + span;
    }
    if (right > hi) {
        right = hi;
        left = hi - span;
    }
    return [new Date(left), new Date(right)];
}
